import React, { useState } from 'react';
import { X, Loader2, Eye, EyeOff } from 'lucide-react';

const InputField = ({ label, id, className = '', ...props }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    <input
      id={id}
      {...props}
      className={`mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${className}`}
    />
  </div>
);

const PasswordField = ({ label, id, value, onChange, autoComplete, disabled, placeholder }) => {
  const [show, setShow] = useState(false);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      <div className="relative mt-1">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          disabled={disabled}
          placeholder={placeholder}
          className="hide-edge-password block w-full px-3 py-2 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />

        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          aria-label={show ? 'Hide password' : 'Show password'}
          disabled={disabled}
        >
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

const UserProfileModal = ({ user, onClose, api, onUpdateSuccess }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user?.userId) {
      setError('User data is not fully loaded. Cannot proceed with password change.');
      return;
    }

    const changingPassword = newPassword.length > 0;

    if (changingPassword) {
      if (!oldPassword || !newPassword || !confirmPassword) {
        setError('To change your password, you must fill in Old Password, New Password, and Confirm New Password.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('New passwords do not match.');
        return;
      }
      if (newPassword.length < 6) {
        setError('New password must be at least 6 characters long.');
        return;
      }
    }

    if (!changingPassword) {
      setError('No changes to save.');
      return;
    }

    setLoading(true);
    try {
      const updatePayload = { oldPassword, newPassword };
      const response = await api.put(`/users/${user.userId}/change-password`, updatePayload);

      setSuccess('Password updated successfully!');
      setNewPassword('');
      setConfirmPassword('');
      setOldPassword('');

      if (onUpdateSuccess) onUpdateSuccess(response.data);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update profile. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex justify-center items-center">
      {/*
        Edge (and some other browsers) can show a built-in password reveal icon.
        This hides it so our custom eye button is the only one shown.
      */}
      <style>{`
        input.hide-edge-password::-ms-reveal,
        input.hide-edge-password::-ms-clear {
          display: none;
        }
        input.hide-edge-password::-webkit-credentials-auto-fill-button {
          visibility: hidden;
          display: none !important;
          pointer-events: none;
          position: absolute;
          right: 0;
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg m-4 transform transition-all">
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">User Profile & Password Change</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form className="p-5 space-y-4" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          {success && (
            <div
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative"
              role="alert"
            >
              <span className="block sm:inline">{success}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center space-x-2 bg-indigo-100 px-3 py-1.5 rounded-full shadow-sm">
              <span className="text-xs font-medium text-indigo-700 uppercase tracking-wider">Username:</span>
              <span className="text-sm text-gray-900 font-semibold truncate">{user.username}</span>
            </div>

            <div className="flex items-center space-x-2 bg-purple-100 px-3 py-1.5 rounded-full shadow-sm">
              <span className="text-xs font-medium text-purple-700 uppercase tracking-wider">Role:</span>
              <span className="text-sm text-gray-900 font-semibold">{user.role}</span>
            </div>
          </div>

          <h4 className="text-lg font-semibold text-gray-900 pt-2 border-t mt-4">Change Password</h4>

          <PasswordField
            label="Old Password"
            id="oldPassword"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
          />

          <PasswordField
            label="New Password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            disabled={loading}
          />

          <PasswordField
            label="Confirm New Password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={loading}
          />

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="relative flex items-center justify-center px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
              disabled={loading}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? 'Updating...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfileModal;
